import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  split
} from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import * as Sentry from '@sentry/react';
import { RestLink } from 'apollo-link-rest';
import { getOperationAST } from 'graphql';
import { disableFragmentWarnings } from 'graphql-tag';

import { env } from '@/constant/env';
import { SpanType } from '../../tracing';
import errorLink from './errorLink';
import { typePolicies } from './traceTypePolicies';

// Disable warning if multiple fragments have the same name.
disableFragmentWarnings();

// tell the browser to send the authentication
// cookie along with the request.
const httpLink = new HttpLink({
  uri: `${env.VITE_API_URL}/graphql`,
  credentials: 'include',
  fetch: (uri, options) =>
    Sentry.startSpan(
      {
        name: uri,
        op: SpanType.reqOut
      },
      () => fetch(uri, options)
    )
});

const restLink = new RestLink({
  uri: env.VITE_API_URL,
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json'
  },
  // @ts-expect-error - should be removed
  fetch: (uri, options) =>
    Sentry.startSpan(
      {
        name: uri,
        op: SpanType.reqOut
      },
      () => fetch(uri, options)
    )
});

// The browser WebSocket constructor requires a ws:// or wss:// URL, but
// VITE_API_URL is an http(s) URL, so convert the scheme (http -> ws,
// https -> wss). Auth is handled server-side from the cookie sent on the
// upgrade request (see tokenMiddleware), so no connectionParams are needed.
const webSocketLink = new WebSocketLink({
  uri: `${env.VITE_API_URL}/graphql`.replace(/^http/, 'ws'),
  options: {
    lazy: true,
    reconnect: true
  }
});

const wsLinkSplitter = operation => {
  // check if it is a subscription
  const operationAST = getOperationAST(
    operation.query,
    operation.operationName
  );
  return !!operationAST && operationAST.operation === 'subscription';
};

const splitLink = split(
  wsLinkSplitter,
  webSocketLink,
  ApolloLink.from([errorLink, restLink, httpLink])
);

export default new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({ typePolicies }),
  connectToDevTools: true
});
